import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cases, persons } from "@/lib/endpoints";
import type { GenerateResponse, JobResponse, PersonResponse } from "@/lib/api-types";
import { cn } from "@/lib/utils";
import { ApiError } from "@/lib/api";
import { humanizeIssueKey } from "@/lib/validation-errors";
import { FieldHelpButton } from "@/components/FieldHelpButton";

/**
 * Final wizard step — Review & Generate.
 *
 * Flow:
 *   Unpaid → blurred N-400 preview + "Unlock" CTA → /checkout.
 *   Paid   → evaluation + generate / download flow.
 */
export function StepReview() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const caseQ = useQuery({
    queryKey: ["case", id],
    queryFn: () => cases.get(id!),
    enabled: !!id,
    staleTime: 0,
  });

  const personsQ = useQuery({
    queryKey: ["case", id, "persons"],
    queryFn: () => persons.list(id!),
    enabled: !!id,
  });

  const applicant = personsQ.data?.items.find(
    (p: PersonResponse) => p.role === "applicant",
  );

  const displayName =
    [applicant?.given_name, applicant?.family_name].filter(Boolean).join(" ").toUpperCase() ||
    "YOUR NAME";

  const isPaid = !!caseQ.data?.paid_at;
  const feeCents = caseQ.data?.fee_total_cents ?? 71000;
  const feeDollars = (feeCents / 100).toFixed(2);

  const evalQ = useQuery({
    queryKey: ["case", id, "evaluation"],
    queryFn: () => cases.evaluation(id!),
    enabled: !!id && isPaid,
  });

  const jobsQ = useQuery({
    queryKey: ["case", id, "jobs"],
    queryFn: () => cases.listJobs(id!),
    enabled: !!id && isPaid,
    staleTime: 0,
  });

  useEffect(() => {
    if (activeJobId) return;
    const latest = jobsQ.data?.items.find((j) => j.queue === "pdf.render");
    if (latest && (latest.state === "pending" || latest.state === "running" || latest.state === "done")) {
      setActiveJobId(latest.id);
    }
  }, [jobsQ.data, activeJobId]);

  const resumeLink = useMutation({
    mutationFn: () => cases.sendResumeLink(id!),
  });

  const generate = useMutation<GenerateResponse, Error, void>({
    mutationFn: () => cases.generate(id!),
    onSuccess: (resp) => {
      setActiveJobId(resp.job_id);
      qc.invalidateQueries({ queryKey: ["case", id, "jobs"] });
      // After a successful enqueue (or short-circuit) the packet
      // is no longer outdated — refresh the status query so the
      // Re-generate button hides immediately instead of after the
      // 30s staleTime.
      qc.invalidateQueries({ queryKey: ["case", id, "packet-status"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        qc.invalidateQueries({ queryKey: ["case", id, "jobs"] });
      }
    },
  });

  const jobQ = useQuery({
    queryKey: ["case", id, "job", activeJobId],
    queryFn: () => cases.getJob(id!, activeJobId!),
    enabled: !!id && !!activeJobId,
    refetchInterval: (q) => {
      const data = q.state.data as JobResponse | undefined;
      if (!data) return 2000;
      if (data.state === "done" || data.state === "failed") return false;
      return 2000;
    },
  });

  const ready = evalQ.data?.ready ?? false;
  const job = jobQ.data;

  // ── Unpaid ─────────────────────────────────────────────────────────────────
  if (!caseQ.isLoading && !isPaid) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold font-display text-slate-900">Review & generate</h2>
          <p className="text-slate-500 mt-1 text-sm leading-relaxed">
            Your N-400 is assembled. Pay the USCIS filing fee to unlock and download your packet.
          </p>
        </div>

        <BlurredPreview caseId={id!} displayName={displayName} feeDollars={feeDollars} />

        <NavRow caseId={id!} />
      </div>
    );
  }

  // ── Paid ───────────────────────────────────────────────────────────────────
  const hasActiveJob =
    job &&
    (job.state === "pending" || job.state === "running" ||
     job.state === "done" || job.state === "failed");

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold font-display text-slate-900">Review & generate</h2>
        <p className="text-slate-500 mt-1 text-sm">
          One last check, then we'll fill in your USCIS-ready N-400 PDF.
        </p>
      </div>

      <ReadinessCard
        ready={ready}
        missingCount={evalQ.data?.missing_data.length ?? 0}
        warnings={evalQ.data?.warnings ?? []}
        missing={evalQ.data?.missing_data ?? []}
        formatErrors={evalQ.data?.format_errors ?? []}
        loading={evalQ.isLoading}
      />

      {!hasActiveJob ? (
        <GenerateCTA
          ready={ready}
          loading={generate.isPending}
          onClick={() => generate.mutate()}
          error={
            generate.error instanceof ApiError && generate.error.status === 409
              ? "A render is already in progress."
              : generate.error instanceof ApiError && generate.error.status === 422
                ? (() => {
                    // 422 from generate carries both missing_data and
                    // format_errors. Stitch them into one human line —
                    // detailed listing is already in ReadinessCard, so
                    // the CTA error stays short.
                    const body = generate.error.body as {
                      missing_data?: { message: string }[];
                      format_errors?: { message: string }[];
                    } | null;
                    const missing = body?.missing_data ?? [];
                    const formats = body?.format_errors ?? [];
                    if (missing.length === 0 && formats.length > 0) {
                      return `${formats.length} stored value${formats.length === 1 ? "" : "s"} look${formats.length === 1 ? "s" : ""} wrong — see the checklist above.`;
                    }
                    if (missing.length > 0) {
                      return `Application incomplete: ${missing.map((i) => i.message).join("; ")}`;
                    }
                    return generate.error.message;
                  })()
                : generate.error?.message
          }
        />
      ) : (
        <JobStatusCard
          job={job}
          caseId={id!}
          onRetry={() => generate.mutate()}
          regenerating={generate.isPending}
        />
      )}

      <ResumeLinkButton
        loading={resumeLink.isPending}
        sent={resumeLink.isSuccess}
        email={resumeLink.data?.to}
        onClick={() => resumeLink.mutate()}
      />

      <NavRow caseId={id!} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Blurred preview (unpaid)
// ---------------------------------------------------------------------------

function BlurredPreview({
  caseId,
  displayName,
  feeDollars,
}: {
  caseId: string;
  displayName: string;
  feeDollars: string;
}) {
  const navigate = useNavigate();

  return (
    <div className="relative rounded-2xl overflow-hidden border border-slate-200/80 shadow-card select-none">
      {/* Fake N-400 (blurred) */}
      <div className="blur-md pointer-events-none bg-white px-8 py-6 text-[11px] leading-tight font-mono">
        <div className="flex items-center justify-between border-2 border-black px-3 py-2 mb-4">
          <div>
            <div className="text-[9px] font-bold tracking-widest">USCIS</div>
            <div className="text-[7px]">Form N-400 (Rev. 04/01/24)</div>
          </div>
          <div className="text-center">
            <div className="text-sm font-bold tracking-tight">Form N-400</div>
            <div className="text-[9px]">Application for Naturalization</div>
          </div>
          <div className="text-right text-[7px] text-slate-400 leading-relaxed">
            OMB No. 1615-0052<br />Expires 03/31/2027
          </div>
        </div>

        <div className="mb-3">
          <div className="bg-slate-800 text-white text-[9px] font-bold px-2 py-1 mb-2">
            Part 1. Information About Your Eligibility
          </div>
          <div className="flex gap-2 text-[9px]">
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 border border-black inline-block bg-slate-800" />
              Have been a LPR for at least 5 years
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 border border-black inline-block" />
              Married to U.S. citizen (3 years)
            </div>
          </div>
        </div>

        <div className="mb-3">
          <div className="bg-slate-800 text-white text-[9px] font-bold px-2 py-1 mb-2">
            Part 2. Information About You
          </div>
          <div className="grid grid-cols-3 gap-3 mb-2">
            <FormField label="Family Name (Last Name)" value={displayName.split(" ").slice(-1)[0] || displayName} highlight />
            <FormField label="Given Name (First Name)" value={displayName.split(" ").slice(0, -1).join(" ") || "—"} highlight />
            <FormField label="Middle Name" value="—" />
          </div>
          <div className="grid grid-cols-3 gap-3 mb-2">
            <FormField label="Date of Birth (mm/dd/yyyy)" value="██/██/████" />
            <FormField label="Country of Birth" value="███████████" />
            <FormField label="A-Number" value="A-█████████" />
          </div>
        </div>

        <div className="mb-3">
          <div className="bg-slate-800 text-white text-[9px] font-bold px-2 py-1 mb-2">
            Part 3. Biographic Information
          </div>
          <div className="grid grid-cols-4 gap-2">
            <FormField label="Ethnicity" value="████████" />
            <FormField label="Race" value="████" />
            <FormField label="Height" value={'█\'██"'} />
            <FormField label="Weight (lbs.)" value="███" />
          </div>
        </div>

        <div className="space-y-1.5 mt-4 opacity-40">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-2 rounded bg-slate-300" style={{ width: `${65 + (i % 3) * 12}%` }} />
          ))}
        </div>

        <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none rotate-[-30deg]">
          <span className="text-6xl font-black tracking-widest text-slate-900 font-display">PREVIEW</span>
        </div>
      </div>

      {/* Frosted overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-[2px]">
        <div className="bg-white/95 border border-slate-200/80 rounded-2xl px-8 py-7 shadow-card text-center max-w-xs mx-4 animate-pop-in">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-teal-500 flex items-center justify-center mx-auto mb-4 shadow-sm">
            <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-bold font-display text-slate-900 leading-snug">Your N-400 is ready</h3>
          <p className="text-sm text-slate-500 mt-2 leading-relaxed">
            We've filled in all your details. Pay the USCIS fee to unlock and download your packet.
          </p>
          <div className="flex items-baseline justify-center gap-1 mt-4">
            <span className="text-3xl font-bold font-display tabular-nums text-slate-900">${feeDollars}</span>
            <span className="text-sm text-slate-400">USCIS fee</span>
          </div>
          <button
            type="button"
            onClick={() => navigate(`/cases/${caseId}/wizard/checkout`)}
            className="mt-5 w-full rounded-xl bg-slate-900 text-white px-4 py-3 text-sm font-semibold hover:bg-slate-800 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
          >
            Pay & unlock →
          </button>
          <p className="text-xs text-slate-400 mt-3 flex items-center justify-center gap-1">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Secured by Stripe
          </p>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div>
      <div className="text-[8px] text-slate-500 mb-0.5">{label}</div>
      <div className={cn("border-b border-black pb-0.5 text-[10px] font-semibold", highlight ? "text-slate-900" : "text-slate-600")}>
        {value}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Readiness card
// ---------------------------------------------------------------------------

function ReadinessCard({
  ready, missingCount, warnings, missing, formatErrors, loading,
}: {
  ready: boolean;
  missingCount: number;
  warnings: { key: string; message: string }[];
  missing: { key: string; message: string }[];
  formatErrors: { key: string; message: string }[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-card">
        <div className="flex items-center gap-3">
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600 shrink-0" />
          <span className="text-sm text-slate-500">Checking your application…</span>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      "rounded-2xl border p-5 shadow-card",
      ready ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50",
    )}>
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={cn(
          "w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5",
          ready ? "bg-emerald-500" : "bg-amber-400",
        )}>
          {ready ? (
            <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className={cn("text-sm font-semibold", ready ? "text-emerald-800" : "text-amber-800")}>
            {ready
              ? "Application complete — ready to generate"
              : formatErrors.length > 0 && missingCount === 0
                ? `${formatErrors.length} stored value${formatErrors.length === 1 ? "" : "s"} look${formatErrors.length === 1 ? "s" : ""} wrong — fix before generating`
                : `${missingCount} item${missingCount === 1 ? "" : "s"} still needed before generating`}
          </p>

          {!ready && missing.length > 0 && (
            <ul className="mt-2.5 space-y-1">
              {missing.map((m) => (
                <li key={m.key} className="flex items-start gap-1.5 text-xs text-amber-700">
                  <span className="mt-0.5 shrink-0">·</span>
                  <span>{m.message}</span>
                  <FieldHelpButton question={`Why is "${m.message}" required on my N-400?`} />
                </li>
              ))}
            </ul>
          )}

          {/* Format errors — values that exist but don't pass the
              format checks the server runs before render. Different
              wording from "missing" because the data IS there, it
              just doesn't fit the schema (bad country code, etc.). */}
          {formatErrors.length > 0 && (
            <ul className="mt-2.5 space-y-1">
              {formatErrors.map((f) => (
                <li key={f.key} className="flex items-start gap-1.5 text-xs text-red-700">
                  <span className="mt-0.5 shrink-0">⚠</span>
                  <span>
                    <span className="font-medium">{humanizeIssueKey(f.key)}</span>
                    {" — "}
                    {f.message}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {warnings.length > 0 && (
            <details className="mt-3">
              <summary className={cn(
                "cursor-pointer text-xs font-medium select-none",
                ready ? "text-emerald-700 hover:text-emerald-800" : "text-amber-700 hover:text-amber-800",
              )}>
                {warnings.length} warning{warnings.length === 1 ? "" : "s"} — click to expand
              </summary>
              <ul className="mt-2 space-y-1">
                {warnings.map((w) => (
                  <li key={w.key} className="flex items-start gap-1.5 text-xs text-slate-600">
                    <span className="mt-0.5 shrink-0">⚠</span>
                    <span>{w.message}</span>
                    <FieldHelpButton question={`What does this N-400 warning mean: "${w.message}"?`} />
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generate CTA
// ---------------------------------------------------------------------------

function GenerateCTA({
  ready, loading, onClick, error,
}: {
  ready: boolean;
  loading: boolean;
  onClick: () => void;
  error?: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-card">
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 to-teal-500 flex items-center justify-center shrink-0 shadow-sm">
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-semibold font-display text-slate-900">Generate your N-400 packet</h3>
          <p className="text-sm text-slate-500 mt-0.5 leading-relaxed">
            We'll fill in the official USCIS N-400 PDF with your answers. Usually ready in under a minute.
          </p>

          {error && (
            <p className="text-xs text-red-600 mt-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
          )}

          <button
            type="button"
            disabled={!ready || loading}
            onClick={onClick}
            className="mt-4 rounded-xl bg-slate-900 text-white px-5 py-2.5 text-sm font-semibold hover:bg-slate-800 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed disabled:translate-y-0"
          >
            {loading ? "Starting…" : ready ? "Generate packet →" : "Complete missing items first"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Job status cards
// ---------------------------------------------------------------------------

function JobStatusCard({
  job,
  caseId,
  onRetry,
  regenerating,
}: {
  job: JobResponse;
  caseId: string;
  onRetry: () => void;
  regenerating: boolean;
}) {
  if (job.state === "pending" || job.state === "running") {
    return <RenderingCard state={job.state} />;
  }
  if (job.state === "failed") {
    return <FailedCard error={job.last_error} onRetry={onRetry} />;
  }
  // `onRetry` doubles as "regenerate the packet with the latest data".
  // The Phase A versioning logic on the backend bumps packet_version
  // when the snapshot has changed, so a re-generate after a typo fix
  // produces a fresh PDF instead of returning the cached one.
  return (
    <DownloadCard
      caseId={caseId}
      jobId={job.id}
      packetVersion={job.packet_version ?? 0}
      onRegenerate={onRetry}
      regenerating={regenerating}
    />
  );
}

function RenderingCard({ state }: { state: "pending" | "running" }) {
  return (
    <div className="rounded-2xl border border-blue-200 bg-blue-50 p-6 shadow-card">
      <div className="flex items-center gap-3 mb-4">
        <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600 shrink-0" />
        <div>
          <p className="font-semibold font-display text-blue-900 text-sm">
            {state === "pending" ? "Queued for rendering…" : "Generating your packet…"}
          </p>
          <p className="text-xs text-blue-700 mt-0.5">
            {state === "pending"
              ? "Your job is next in queue."
              : "Filling in your N-400 form fields. Usually takes 10–30 seconds."}
          </p>
        </div>
      </div>

      {/* Animated progress bar */}
      <div className="h-1.5 bg-blue-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-blue-600 rounded-full animate-pulse"
          style={{ width: state === "running" ? "65%" : "20%", transition: "width 2s ease" }}
        />
      </div>

      <p className="text-[11px] text-blue-600 mt-2 text-right">
        This page will update automatically.
      </p>
    </div>
  );
}

function FailedCard({ error, onRetry }: { error?: string | null; onRetry: () => void }) {
  return (
    <div className="rounded-2xl border border-red-200 bg-red-50 p-6 shadow-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-7 h-7 rounded-full bg-red-500 flex items-center justify-center shrink-0 mt-0.5">
          <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <div>
          <p className="font-semibold font-display text-red-900 text-sm">Render failed</p>
          <p className="text-xs text-red-700 mt-0.5">{error ?? "An unexpected error occurred. Please try again."}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-xl bg-slate-900 text-white px-5 py-2.5 text-sm font-semibold hover:bg-slate-800 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
      >
        Try again →
      </button>
    </div>
  );
}

function DownloadCard({
  caseId,
  jobId,
  packetVersion,
  onRegenerate,
  regenerating,
}: {
  caseId: string;
  jobId: string;
  packetVersion: number;
  onRegenerate: () => void;
  regenerating: boolean;
}) {
  const dl = useQuery({
    queryKey: ["case", caseId, "job", jobId, "download"],
    queryFn: () => cases.jobDownload(caseId, jobId),
    refetchInterval: 9 * 60 * 1000, // refresh URL before 10-min expiry
  });
  // Hide the Re-generate button when the snapshot hasn't changed
  // since the last render — clicking it would no-op. Backend's
  // /packet-status computes the snapshot hash and compares against
  // case.last_snapshot_hash.
  //
  // refetchOnMount: "always" + staleTime: 0 — the wizard mutations
  // don't all individually invalidate this query, so we rely on a
  // fresh fetch every time the user lands on /review. The hash
  // computation is light (~50ms); one extra round-trip per mount is
  // a fair price for "button reflects reality".
  const status = useQuery({
    queryKey: ["case", caseId, "packet-status"],
    queryFn: () => cases.packetStatus(caseId),
    staleTime: 0,
    refetchOnMount: "always",
  });
  const showRegenerate = status.data?.is_outdated === true;

  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 shadow-card">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center shrink-0 shadow-sm">
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <p className="font-bold font-display text-emerald-900">Your N-400 packet is ready!</p>
            {packetVersion > 0 && (
              <span
                className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded"
                title={`This is the ${ordinal(packetVersion)} time you've generated this packet.`}
              >
                v{packetVersion}
              </span>
            )}
          </div>
          <p className="text-xs text-emerald-700 mt-0.5">Review every field carefully before submitting to USCIS.</p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-white/60 border border-emerald-200 rounded-xl px-4 py-3 mb-5">
        <p className="text-xs text-emerald-800 leading-relaxed">
          <strong>Important:</strong> This PDF is generated from the information you provided.
          Always review the completed form before mailing it to USCIS. For complex cases, consider
          having a qualified immigration attorney review your packet.
        </p>
        <p className="text-xs text-emerald-700 mt-2 flex items-center gap-1">
          Not sure what to do next?
          <FieldHelpButton question="I downloaded my N-400 PDF. What do I do now? How do I submit it to USCIS?" />
        </p>
      </div>

      {/* Download + regenerate buttons */}
      <div className="flex flex-wrap items-center gap-3">
        {dl.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-emerald-700">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-emerald-300 border-t-emerald-600" />
            Preparing download link…
          </div>
        ) : dl.error ? (
          <p className="text-sm text-red-600">{(dl.error as Error).message}</p>
        ) : dl.data ? (
          <>
            <a
              href={dl.data.download_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 text-white px-5 py-2.5 text-sm font-semibold hover:bg-slate-800 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download N-400 PDF
            </a>
            <span className="text-[11px] text-emerald-600">Link expires in ~10 min</span>
          </>
        ) : null}
      </div>

      {/* Regenerate — visible only when the snapshot has changed since
          the last render. The backend's packet_version bumps when the
          snapshot has changed, so clicking produces a new PDF (not the
          cached one). When the snapshot is unchanged, clicking would
          no-op, so the button stays hidden to avoid confusing users. */}
      {showRegenerate && (
        <div className="mt-4 pt-4 border-t border-emerald-200/60">
          <p className="text-xs text-emerald-800 mb-2">
            You've changed something since the last render. Re-run the
            form-fill with your latest answers — the previous PDF stays
            available until the new one is ready.
          </p>
          <button
            type="button"
            onClick={onRegenerate}
            disabled={regenerating}
            className="inline-flex items-center gap-2 rounded-xl bg-white text-emerald-800 border border-emerald-300 px-4 py-2 text-sm font-semibold hover:bg-emerald-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h5M20 20v-5h-5M5 20A9 9 0 0119 5M19 4a9 9 0 01-14 15" />
            </svg>
            {regenerating ? "Starting re-render…" : "Re-generate packet"}
          </button>
        </div>
      )}
    </div>
  );
}

// ordinal turns 1/2/3/4/… into "1st/2nd/3rd/4th/…". English-only by
// design — the alpha banner already commits us to English copy.
function ordinal(n: number): string {
  if (n <= 0) return String(n);
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

// ---------------------------------------------------------------------------
// Resume link button
// ---------------------------------------------------------------------------

function ResumeLinkButton({
  loading, sent, email, onClick,
}: {
  loading: boolean;
  sent: boolean;
  email?: string;
  onClick: () => void;
}) {
  return (
    <div className="flex items-center justify-center pt-1">
      {sent ? (
        <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          Link sent{email ? ` to ${email}` : ""}
        </span>
      ) : (
        <button
          type="button"
          onClick={onClick}
          disabled={loading}
          className="text-xs text-slate-400 hover:text-blue-600 transition-colors disabled:opacity-50"
        >
          {loading ? "Sending…" : "✉ Email me a link to continue later"}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared nav row
// ---------------------------------------------------------------------------

function NavRow({ caseId }: { caseId: string }) {
  return (
    <div className="flex items-center justify-between pt-4 border-t border-slate-200">
      <Link to={`/cases/${caseId}/wizard/oath`} className="text-sm text-slate-500 hover:text-slate-900 transition-colors">
        ← Oath
      </Link>
      <Link to={`/cases/${caseId}`} className="text-sm text-slate-400 hover:text-slate-600 transition-colors">
        Case overview
      </Link>
    </div>
  );
}
